import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Briefcase,
  Users,
  ClipboardList,
  Shield,
  CheckCircle2,
  Rocket,
  Sparkles,
  ArrowRight,
  Camera,
  Mic,
  Globe,
  Wallet,
  Download
} from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to ConstructHelp!',
    subtitle: 'A quick tour of your new tools',
    description: "In about 90 seconds, we'll show you the 6 features that will save you the most time — including AI Photo Estimating, Voice Translator, and your shareable public profile.",
    icon: Rocket,
    color: 'from-blue-500 to-indigo-600',
    image: null,
  },
  {
    id: 'ai-pricing',
    title: 'NEW · AI Photo Estimator',
    subtitle: 'Snap a photo — get a scoped estimate',
    description: 'Upload up to 5 job-site photos and GPT-5.2 Vision will return a 3-tier estimate (Economy / Standard / Premium) with a line-item materials list. Bid faster, win more jobs.',
    icon: Camera,
    color: 'from-amber-500 to-orange-600',
    tips: [
      'Capture damage, scope, and existing materials',
      'AI suggests labor hours and crew size',
      '3 pricing tiers ready to send to homeowners',
      'Save estimates with photos for later reference'
    ],
    action: { label: 'Open AI Pricing Tool', path: '/ai-pricing' }
  },
  {
    id: 'translator',
    title: 'NEW · Voice Translator',
    subtitle: 'English ↔ Spanish, instant',
    description: 'Tap the mic, speak English or Spanish, and hear the translation back in seconds. Built for the job site — trade jargon stays accurate (rebar ↔ varilla, drywall ↔ tablaroca).',
    icon: Mic,
    color: 'from-emerald-500 to-teal-600',
    tips: [
      'Talk to any worker, any language',
      'Whisper transcribes, GPT-5.2 translates, TTS speaks',
      'Construction terms preserved exactly',
      'Free, included with every contractor account'
    ],
    action: { label: 'Open Translator', path: '/translator' }
  },
  {
    id: 'leads-and-jobs',
    title: 'Find Work: Post Jobs & Buy Leads',
    subtitle: 'Two ways to grow your pipeline',
    description: 'Post a job and let qualified workers come to you, OR browse the Leads Marketplace for verified homeowner projects you can claim instantly. Pay only for leads you actually buy.',
    icon: Briefcase,
    color: 'from-orange-500 to-amber-600',
    tips: [
      'Post jobs free — set budget and required skills',
      'Browse Leads Marketplace for live homeowner projects',
      'Pay only when you claim a lead',
      'Check the Wallet to track lead spend'
    ],
    action: { label: 'Browse Leads', path: '/leads-marketplace' }
  },
  {
    id: 'public-profile',
    title: 'NEW · Your Shareable Profile',
    subtitle: 'Hand out a one-tap business card',
    description: 'Every contractor gets a public profile at /pro/your-name with reviews, portfolio, and Hire-Me CTA. Share the link on Google, Yelp, business cards, or socials — rich previews on every platform.',
    icon: Globe,
    color: 'from-cyan-500 to-blue-600',
    tips: [
      'OpenGraph + Twitter Cards built in',
      'Free SEO traffic to your services',
      'Verified Pro badge after license/insurance check',
      'Copy/Share button on your dashboard'
    ],
    action: { label: 'View My Profile', path: '/contractor-dashboard' }
  },
  {
    id: 'secure-payments',
    title: 'Secure Payments & Bank Payouts',
    subtitle: 'Escrow + Stripe Connect to your bank',
    description: 'Homeowners fund escrow before work starts. As you hit milestones, money releases to your wallet — then withdraw to your bank via Stripe Connect.',
    icon: Wallet,
    color: 'from-green-500 to-emerald-600',
    tips: [
      'Funds held safely until work is approved',
      'Milestone-based release schedule',
      'Connect your bank — direct payouts',
      'Track wallet balance + transaction history'
    ],
    action: { label: 'Open Wallet', path: '/wallet' }
  },
  {
    id: 'install-pwa',
    title: 'Install on Your Phone',
    subtitle: 'One tap from your home screen',
    description: 'ConstructHelp works as an installable app. Get faster cold-starts, full-screen mode, and push notifications — built for life on a job site. Look for the "Install" prompt at the bottom-right of your screen.',
    icon: Download,
    color: 'from-purple-500 to-violet-600',
    tips: [
      'Works offline for cached pages',
      'Long-press the icon for quick shortcuts',
      'Push notifications for new leads',
      'On iPhone: Safari Share → Add to Home Screen'
    ],
    action: null
  },
  {
    id: 'complete',
    title: "You're All Set!",
    subtitle: 'Start building your projects',
    description: "You now know the essentials of ConstructHelp. You can always restart this tour from Settings or open the AI tools from your dashboard hero tiles.",
    icon: CheckCircle2,
    color: 'from-green-500 to-emerald-600',
    image: null,
  }
];

const OnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') handleSkip(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  const step = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const Icon = step.icon;

  const goToNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setAnimating(false);
    }, 200);
  };

  const goToPrev = () => {
    if (isFirstStep) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setAnimating(false);
    }, 200);
  };

  const handleComplete = () => {
    // Mark tutorial as completed in localStorage
    localStorage.setItem('constructhelp_onboarding_completed', 'true');
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('constructhelp_onboarding_completed', 'true');
    onClose();
  };

  const handleAction = (path) => {
    handleComplete();
    navigate(path);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden transform transition-all duration-300 ${animating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
          <div 
            className={`h-full bg-gradient-to-r ${step.color} transition-all duration-500`}
            style={{ width: `${((currentStep + 1) / TUTORIAL_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
          title="Skip tutorial"
          aria-label="Close tour"
          data-testid="tour-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="pt-8 pb-6 px-8">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {TUTORIAL_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? 'w-8 bg-gradient-to-r ' + step.color
                    : idx < currentStep
                    ? 'w-2 bg-green-500'
                    : 'w-2 bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
            <Icon className="h-10 w-10 text-white" />
          </div>

          {/* Title & Description */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {step.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-3">
              {step.subtitle}
            </p>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {step.description}
            </p>
          </div>

          {/* Tips */}
          {step.tips && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-yellow-500" />
                Pro Tips:
              </p>
              <ul className="space-y-2">
                {step.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Button (for middle steps) */}
          {step.action && (
            <div className="text-center mb-6">
              <Button
                onClick={() => handleAction(step.action.path)}
                className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white px-6`}
              >
                {step.action.label}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                Or continue the tutorial first
              </p>
            </div>
          )}

          {/* Welcome Animation */}
          {step.id === 'welcome' && (
            <div className="flex justify-center gap-3 mb-6 flex-wrap">
              {[Camera, Mic, Briefcase, Globe, Wallet, Download].map((ItemIcon, idx) => (
                <div 
                  key={idx}
                  className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center"
                  style={{ animation: `bounce 1s ease-in-out ${idx * 0.1}s infinite` }}
                >
                  <ItemIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                </div>
              ))}
            </div>
          )}

          {/* Completion Celebration */}
          {step.id === 'complete' && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full px-4 py-2">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-semibold">Tutorial Complete!</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="px-8 py-4 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goToPrev}
            disabled={isFirstStep}
            className={isFirstStep ? 'invisible' : ''}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <Button
            onClick={goToNext}
            className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white`}
          >
            {isLastStep ? (
              <>
                Get Started
                <Rocket className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingTutorial;
